import type {
  ApiConversation,
  ApiConversationUser,
  ApiNotification,
  ApiPatternRef,
  CreatorNotification,
  CustomerMessage,
  NotificationCategory,
} from "../types/messages.types";

export const formatRelativeTime = (value: string | Date | null | undefined): string => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
};

export const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const getPopulatedName = (user: string | ApiConversationUser | undefined): string => {
  if (!user) return "Customer";
  if (typeof user === "object" && user.name) return user.name;
  return "Customer";
};

const getPatternName = (pattern: string | ApiPatternRef | null | undefined): string => {
  if (!pattern) return "General inquiry";
  if (typeof pattern === "object" && pattern.name) return pattern.name;
  return "Pattern";
};

export const deriveNotificationCategory = (
  notification: ApiNotification
): NotificationCategory => {
  const combined = `${notification.title} ${notification.message}`.toLowerCase();

  if (combined.includes("review")) {
    return "reviews";
  }

  if (
    notification.type === "PATTERN_PURCHASED" ||
    notification.relatedType === "ORDER"
  ) {
    return "orders";
  }

  return "system";
};

export const mapNotificationToView = (
  notification: ApiNotification
): CreatorNotification => ({
  id: notification._id,
  title: notification.title,
  body: notification.message,
  timestamp: formatRelativeTime(notification.createdAt),
  category: deriveNotificationCategory(notification),
  isRead: notification.isRead,
});

const resolveUserId = (user: string | ApiConversationUser): string =>
  typeof user === "object" ? user._id : user;

export const mapConversationToCustomerMessage = (
  conversation: ApiConversation,
  currentUserId: string
): CustomerMessage => {
  const buyer =
    typeof conversation.buyerId === "object" ? conversation.buyerId : null;
  const creator =
    typeof conversation.creatorId === "object" ? conversation.creatorId : null;

  const creatorId = resolveUserId(conversation.creatorId);
  const isCreator = String(creatorId) === String(currentUserId);

  const otherParty = isCreator ? buyer : creator;
  const senderName = getPopulatedName(otherParty ?? undefined);
  const lastMessage = conversation.lastMessage;

  return {
    id: lastMessage?._id || conversation._id,
    conversationId: conversation._id,
    sender: senderName,
    initials: getInitials(senderName),
    subject: lastMessage?.subject || "Conversation",
    body: lastMessage?.text || "No messages yet",
    product: getPatternName(conversation.patternId),
    timestamp: formatRelativeTime(
      conversation.lastMessageAt || lastMessage?.createdAt
    ),
    isUnread: (conversation.unreadCount ?? 0) > 0,
  };
};
